/** Allowed editor origins — must stay in sync with Web::InstrumentsController#edit. */
export const RETURN_TO_INSTRUMENT_TYPES = 'instrument_types'

export const RETURN_TO_ORIGINS = [RETURN_TO_INSTRUMENT_TYPES] as const

export type InstrumentEditReturnTo = (typeof RETURN_TO_ORIGINS)[number]

/**
 * Mirror server allowlist so a bad/missing value cannot redirect Cancel anywhere
 * unexpected — anything but a recognized origin falls back to the instrument detail
 * page (the default entry point via Instruments/Show).
 */
export const resolveReturnTo = (returnTo: unknown): InstrumentEditReturnTo | null => {
  const candidate = typeof returnTo === 'string' ? returnTo.trim() : returnTo
  return RETURN_TO_ORIGINS.includes(candidate as InstrumentEditReturnTo)
    ? (candidate as InstrumentEditReturnTo)
    : null
}

/**
 * The Site/Dept/Unit/Tag filters, search text, and expanded type the user had active on
 * the Instrument Types tab when they opened the instrument edit form — round-tripped
 * through Cancel/Save so returning there doesn't reset them (REBUILD-388/JW 386).
 */
export interface InstrumentTypesReturnFilters {
  search: string
  siteIds: string
  departmentIds: string
  unitIds: string
  tagIds: string
  expandedTypeId: string
}

const EMPTY_RETURN_FILTERS: InstrumentTypesReturnFilters = {
  search: '',
  siteIds: '',
  departmentIds: '',
  unitIds: '',
  tagIds: '',
  expandedTypeId: '',
}

/** Maps each InstrumentTypesReturnFilters key to the query param Web::InstrumentsController
 * and Admin::InstrumentTypesController both expect it under. Single source of truth so the
 * two never drift apart. */
const RETURN_FILTER_PARAM_NAMES: Record<keyof InstrumentTypesReturnFilters, string> = {
  search: 'search',
  siteIds: 'site_ids',
  departmentIds: 'department_ids',
  unitIds: 'unit_ids',
  tagIds: 'tag_ids',
  expandedTypeId: 'expanded_type_id',
}

/**
 * Re-allowlists the server-provided `returnFilters` prop (Web::InstrumentsController#edit)
 * before it's echoed back into a URL — mirrors resolveReturnTo's "never trust a raw server
 * prop blindly" stance for the filter values riding alongside return_to.
 */
export const resolveReturnFilters = (returnFilters: unknown): InstrumentTypesReturnFilters => {
  const source = (returnFilters ?? {}) as Record<string, unknown>
  const asString = (value: unknown) => (typeof value === 'string' ? value : '')
  return {
    search: asString(source.search),
    siteIds: asString(source.siteIds),
    departmentIds: asString(source.departmentIds),
    unitIds: asString(source.unitIds),
    tagIds: asString(source.tagIds),
    expandedTypeId: asString(source.expandedTypeId),
  }
}

/**
 * Converts filters into a query-param object (snake_case keys, blanks omitted) suitable for
 * a js-routes path helper's options, e.g. `adminInstrumentTypesPath(instrumentTypesFilterQuery(f))`.
 */
export const instrumentTypesFilterQuery = (
  filters: InstrumentTypesReturnFilters = EMPTY_RETURN_FILTERS,
): Record<string, string> => {
  const query: Record<string, string> = {}
  for (const [key, paramName] of Object.entries(RETURN_FILTER_PARAM_NAMES) as Array<
    [keyof InstrumentTypesReturnFilters, string]
  >) {
    const value = filters[key]
    if (value) query[paramName] = value
  }
  return query
}

/**
 * Appends `return_to` (and, when provided, the current Instrument Types filters) to a
 * server-provided href without disturbing an existing query string. `editInstrumentHref`
 * (from `Web::Admin::InstrumentTypeInstrumentSerializer`) is always a bare path today, but
 * this stays correct if that ever changes — safer than the naive
 * `` `${href}?return_to=${origin}` `` template that breaks on a second `?`.
 */
export const appendReturnTo = (
  href: string,
  origin: InstrumentEditReturnTo,
  filters?: InstrumentTypesReturnFilters,
): string => {
  const params = new URLSearchParams({ return_to: origin, ...instrumentTypesFilterQuery(filters) })
  return `${href}${href.includes('?') ? '&' : '?'}${params.toString()}`
}
